import React from 'react'
import { View } from 'react-native'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Icon, Text } from '@components/base'
import { EhrAppointmentQuery } from '@src/screens/NutritionistHub/graphql/ehrAppointment.generated'
import {
  CoachesCoachingKindDef,
  EhrAppointmentAttendanceChargeKind,
  InsurancePolicy,
} from '@src/types'
import { ReservedCallCard } from '@src/screens/NutritionistHub/components/NextCall/Cards/ReservedCallCard'
import { Chip } from '@src/screens/NutritionistHub/components/NextCall/Cards/Chip'
import { LoadingCard } from '@src/screens/NutritionistHub/components/NextCall/Cards'
import { getCoachingKindDescriptor } from '@src/utils/coach'
import { LoadingIndicator } from './LoadingIndicator'
import { CoachAvatar } from './Avatar/CoachAvatar'

type Coach = {
  id: string
  photo?: string | null
  fullName: string
  coachingKind?: CoachesCoachingKindDef
}

interface AppointmentConfirmedProps {
  appointments: EhrAppointmentQuery['ehrAppointment'][] | null
  coaches?: Coach[] | null
  insurancePolicy?: InsurancePolicy | null
  loadingCardsCount?: number
}

export const AppointmentConfirmed = ({
  appointments,
  coaches,
  insurancePolicy,
  loadingCardsCount = 0,
}: AppointmentConfirmedProps) => {
  const styles = useStyleSheet(themedStyles)

  const loading = !appointments || appointments.length === 0

  return (
    <>
      <Icon name="seal-check" weight="fill" style={styles.checkmark} size={56} />
      <Text type="title-2" style={styles.title}>
        You’re all set!
      </Text>
      {loading ? (
        <LoadingIndicator />
      ) : (
        <>
          {coaches && coaches.length > 0 && (
            <View style={styles.coaches}>
              {coaches.map((coach) => (
                <View style={styles.coachCard} key={coach.id}>
                  <CoachAvatar photo={coach.photo} style={styles.coachPhoto} />
                  <View>
                    <Text type="regular" bold>
                      {coach.fullName}
                    </Text>
                    <Text type="small" style={styles.coachDescription}>
                      {`${getCoachingKindDescriptor(coach.coachingKind).capitalized}`}
                    </Text>
                  </View>
                </View>
              ))}
            </View>
          )}
          {appointments.length > 0 && (
            <>
              <View style={styles.bookedCallHeader}>
                <Icon name="video-camera" weight="bold" />
                <Text type="large" bold>
                  Booked {appointments.length + loadingCardsCount > 1 ? 'Calls' : 'Call'}
                </Text>
              </View>
              <View style={styles.appointments}>
                {appointments.map((appointment) => (
                  <ReservedCallCard
                    key={appointment.id}
                    meetingAt={appointment.meetingAt}
                    kind={appointment.kind}
                    lastStateKind={appointment.lastState.kind}
                    chargeKind={appointment.attendances[0].chargeKind}
                    coachName={appointment.providerName as string}
                    badgeAccessory={
                      [
                        EhrAppointmentAttendanceChargeKind.BsInsuranceCovered,
                        EhrAppointmentAttendanceChargeKind.BsSelfPay,
                      ].includes(appointment.attendances[0]?.chargeKind) &&
                      insurancePolicy &&
                      !insurancePolicy.declined &&
                      insurancePolicy.inReview ? (
                        <Chip label="Insurance" icon="shield-check" style={styles.covered} />
                      ) : undefined
                    }
                  />
                ))}
                {Array.from({ length: loadingCardsCount }).map((_, index) => (
                  <LoadingCard key={index} />
                ))}
              </View>
            </>
          )}
        </>
      )}
    </>
  )
}

const themedStyles = StyleService.create({
  informationText: {
    textAlign: 'center',
    marginTop: 24,
  },
  checkmark: {
    color: 'theme.success.base',
    alignSelf: 'center',
  },
  title: {
    marginTop: 14,
    marginBottom: 24,
    textAlign: 'center',
  },
  coaches: {
    gap: 12,
    width: '100%',
  },
  bookedCallHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    alignSelf: 'flex-start',
    marginTop: 32,
    marginBottom: 16,
  },
  appointments: {
    gap: 8,
    width: '100%',
  },
  coachCard: {
    padding: 16,
    borderRadius: 24,
    borderWidth: 2,
    borderColor: 'theme.surface.base1',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: 'theme.background.selected',
    width: '100%',
  },
  coachPhoto: {
    width: 48,
    height: 48,
    borderRadius: 52,
  },
  coachDescription: {
    color: 'theme.text.secondary',
  },
  covered: {
    backgroundColor: 'theme.success.darkest',
  },
})
